import JSZip from 'jszip';
import {xml2js} from 'xml-js';
import {LatLngLiteral} from 'leaflet';
import {cornersFromLatLonBox} from './latLonBox';

export type ParsedKmz = {
  imageFile: File;
  previewUrl: string;
  width: number;
  height: number;
  topLeft: LatLngLiteral;
  topRight: LatLngLiteral;
  bottomLeft: LatLngLiteral;
  suggestedName: string;
};

function getText(node: unknown): string | undefined {
  if (node === undefined || node === null) return undefined;
  if (typeof node === 'string' || typeof node === 'number') return String(node);
  if (typeof node === 'object' && '_text' in node) return String((node as {_text: string | number})._text);
  return undefined;
}

function collectGroundOverlays(obj: unknown): Record<string, unknown>[] {
  const out: Record<string, unknown>[] = [];
  const walk = (o: unknown) => {
    if (o === null || typeof o !== 'object') return;
    if (Array.isArray(o)) {
      o.forEach(walk);
      return;
    }
    const rec = o as Record<string, unknown>;
    if ('GroundOverlay' in rec) {
      const g = rec.GroundOverlay;
      const arr = Array.isArray(g) ? g : [g];
      for (const item of arr) {
        if (item && typeof item === 'object') out.push(item as Record<string, unknown>);
      }
    }
    for (const v of Object.values(rec)) walk(v);
  };
  walk(obj);
  return out;
}

function getIconHref(overlay: Record<string, unknown>): string | undefined {
  const icon = overlay.Icon;
  if (!icon) return undefined;
  const iconObj = Array.isArray(icon) ? icon[0] : icon;
  if (typeof iconObj !== 'object' || !iconObj) return undefined;
  const o = iconObj as Record<string, unknown>;
  const href = o.href;
  const fromChild = getText(href);
  if (fromChild) return fromChild;
  const attrs = o._attributes as {href?: string} | undefined;
  return attrs?.href;
}

function resolveKmzAssetPath(kmlPath: string, href: string): string {
  const normalized = href.replace(/\\/g, '/').replace(/^\.\//, '');
  if (normalized.startsWith('/')) return normalized.slice(1);
  const dir = kmlPath.includes('/') ? kmlPath.slice(0, kmlPath.lastIndexOf('/')) : '';
  if (!dir) return normalized;
  return `${dir}/${normalized}`.replace(/\/+/g, '/');
}

function cornersFromLatLonBoxRecord(box: Record<string, unknown>): {topLeft: LatLngLiteral; topRight: LatLngLiteral; bottomLeft: LatLngLiteral} {
  const east = parseFloat(getText(box.east) || '');
  const north = parseFloat(getText(box.north) || '');
  const south = parseFloat(getText(box.south) || '');
  const west = parseFloat(getText(box.west) || '');
  if ([east, north, south, west].some((n) => Number.isNaN(n))) {
    throw new Error('Invalid LatLonBox coordinates in KML');
  }
  const rotationDeg = parseFloat(getText(box.rotation) || '0');
  const rot = Number.isNaN(rotationDeg) ? 0 : rotationDeg;
  return cornersFromLatLonBox({north, south, east, west, rotationDeg: rot});
}

async function loadImageDimensions(objectUrl: string): Promise<{width: number; height: number}> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve({width: img.naturalWidth, height: img.naturalHeight});
    img.onerror = () => reject(new Error('Could not read image dimensions'));
    img.src = objectUrl;
  });
}

/**
 * Reads a KMZ (zip with KML + raster), finds the first GroundOverlay with LatLonBox,
 * resolves the overlay image, and returns data for CreateMapPayload (plus preview URL to revoke later).
 */
export async function parseKmzFile(kmzFile: File): Promise<ParsedKmz> {
  const zip = await JSZip.loadAsync(kmzFile);
  const kmlPaths = Object.keys(zip.files).filter((p) => !zip.files[p].dir && p.toLowerCase().endsWith('.kml'));
  if (!kmlPaths.length) {
    throw new Error('No KML file found in KMZ archive');
  }
  const kmlPath =
    kmlPaths.find((p) => /(^|\/)doc\.kml$/i.test(p)) || kmlPaths.sort((a, b) => a.length - b.length)[0];

  const kmlText = await zip.file(kmlPath)?.async('string');
  if (!kmlText) {
    throw new Error('Could not read KML from KMZ');
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const data = xml2js(kmlText, {compact: true}) as any;
  const overlays = collectGroundOverlays(data);
  const overlay = overlays.find((o) => getIconHref(o) && o.LatLonBox);
  if (!overlay) {
    throw new Error('No GroundOverlay with Icon and LatLonBox found in KML');
  }

  const href = getIconHref(overlay);
  if (!href) {
    throw new Error('GroundOverlay has no Icon href');
  }

  const latLonBox = overlay.LatLonBox as Record<string, unknown>;
  const {topLeft, topRight, bottomLeft} = cornersFromLatLonBoxRecord(latLonBox);

  const resolved = resolveKmzAssetPath(kmlPath, href);
  const candidates = [resolved, href.replace(/^\.\//, ''), href.split('/').pop() || href];

  let imageEntry: JSZip.JSZipObject | null = null;
  for (const c of candidates) {
    const f = zip.file(c);
    if (f && !f.dir) {
      imageEntry = f;
      break;
    }
  }
  if (!imageEntry) {
    throw new Error(`Could not find overlay image in KMZ: ${href}`);
  }

  const mimeGuess = (name: string): string => {
    const ext = name.split('.').pop()?.toLowerCase();
    if (ext === 'jpg' || ext === 'jpeg') return 'image/jpeg';
    if (ext === 'png') return 'image/png';
    if (ext === 'gif') return 'image/gif';
    return 'application/octet-stream';
  };

  const blob = await imageEntry.async('blob');
  const baseName = href.split('/').pop() || 'overlay.png';
  const type = blob.type && blob.type !== 'application/octet-stream' ? blob.type : mimeGuess(baseName);
  const imageFile = new File([blob], baseName, {type});

  const previewUrl = URL.createObjectURL(blob);
  try {
    const {width, height} = await loadImageDimensions(previewUrl);
    const suggestedFromKml = getText(overlay.name);
    const suggestedName = suggestedFromKml?.trim() || kmzFile.name.replace(/\.kmz$/i, '');

    return {
      imageFile,
      previewUrl,
      width,
      height,
      topLeft,
      topRight,
      bottomLeft,
      suggestedName
    };
  } catch (e) {
    URL.revokeObjectURL(previewUrl);
    throw e;
  }
}
